import mixpanel from 'mixpanel-browser';
import { User } from '../types/RoleType';
import { isDevelopment, isStaging, isProduction, EnvironmentConfig } from './environment';

/**
 * Mixpanel Analytics Configuration and Utilities
 *
 * Provides comprehensive event tracking for user behavior analysis.
 * Must be initialized in main.tsx or App.tsx before use.
 */

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';

/**
 * Initialize Mixpanel
 * Call this in main.tsx or App.tsx on startup
 */
export function initMixpanel(): void {
  if (!MIXPANEL_TOKEN) {
    console.warn('Mixpanel token not found. Analytics is disabled.');
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: EnvironmentConfig.mixpanelDebugMode(),
    track_pageview: false, // We'll track manually for better control
    persistence: 'localStorage',
    ignore_dnt: false, // Respect Do Not Track
    api_host: 'https://api.mixpanel.com',
    autocapture: true,
    record_sessions_percent: isProduction() ? 10 : 100, // Sample 10% of sessions in production
  });

  // Set super properties (included in every event)
  mixpanel.register({
    environment: ENVIRONMENT,
    is_staging: isStaging(),
    is_production: isProduction(),
    is_development: isDevelopment(),
    app_version: '1.0.0', // TODO: Get from package.json or env var
    timestamp: new Date().toISOString(),
  });

  if (isDevelopment()) {
    console.log('[Mixpanel] Initialized in development mode with debug enabled');
  }
}

/**
 * Identify user in Mixpanel
 * Call this after successful login
 */
export function identifyUser(user: User): void {
  if (!MIXPANEL_TOKEN) return;

  mixpanel.identify(user.id);

  // Set user properties
  mixpanel.people.set({
    $email: user.email,
    $name: `${user.firstName} ${user.lastName}`,
    firstName: user.firstName,
    lastName: user.lastName,
    roleType: user.roleType,
    roles: user.roles,
    instituteName: user.instituteName,
    permissions: user.permissions,
  });

  // Track login event
  trackEvent('User Logged In', {
    roleType: user.roleType,
    roles: user.roles,
    instituteName: user.instituteName,
  });
}

/**
 * Reset Mixpanel identity
 * Call this on logout
 */
export function resetMixpanel(): void {
  if (!MIXPANEL_TOKEN) return;

  // Track logout event before resetting
  trackEvent('User Logged Out', {
    timestamp: new Date().toISOString(),
  });

  mixpanel.reset();
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, properties?: Record<string, any>): void {
  if (!MIXPANEL_TOKEN) {
    return;
  }

  mixpanel.track(eventName, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track page view
 */
export function trackPageView(pageName: string, properties?: Record<string, any>): void {
  if (!MIXPANEL_TOKEN) return;

  trackEvent('Page Viewed', {
    page_name: pageName,
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer,
    ...properties,
  });
}

/**
 * Track user timing (e.g., time spent on a page or completing a task)
 */
export function trackTiming(
  category: string,
  variable: string,
  timeMs: number,
  label?: string
): void {
  if (!MIXPANEL_TOKEN) return;

  trackEvent('Timing', {
    category,
    variable,
    time_ms: timeMs,
    time_seconds: Math.round(timeMs / 1000),
    label,
  });
}

/**
 * Increment a user property
 * Useful for tracking counts (e.g., number of students created)
 */
export function incrementUserProperty(property: string, value: number = 1): void {
  if (!MIXPANEL_TOKEN) return;

  mixpanel.people.increment(property, value);
}

/**
 * Track CRUD operation
 * Standardized event for create/read/update/delete operations
 */
export function trackCRUD(
  action: 'create' | 'read' | 'update' | 'delete',
  entityType: string,
  entityId?: string,
  properties?: Record<string, any>
): void {
  if (!MIXPANEL_TOKEN) return;

  const eventName = `${entityType} ${action.charAt(0).toUpperCase() + action.slice(1)}d`;

  trackEvent(eventName, {
    action,
    entity_type: entityType,
    entity_id: entityId,
    ...properties,
  });
}

/**
 * Track form interaction
 */
export function trackFormEvent(
  action: 'opened' | 'submitted' | 'error' | 'abandoned',
  formName: string,
  properties?: Record<string, any>
): void {
  if (!MIXPANEL_TOKEN) return;

  const eventMap = {
    opened: 'Form Opened',
    submitted: 'Form Submitted',
    error: 'Form Validation Error',
    abandoned: 'Form Abandoned',
  };

  trackEvent(eventMap[action], {
    form_name: formName,
    ...properties,
  });
}

/**
 * Track search
 */
export function trackSearch(
  query: string,
  resultsCount: number,
  entityType?: string,
  filters?: Record<string, any>
): void {
  if (!MIXPANEL_TOKEN) return;

  trackEvent('Search Performed', {
    query,
    results_count: resultsCount,
    entity_type: entityType,
    has_results: resultsCount > 0,
    filters_applied: filters ? Object.keys(filters).length : 0,
    ...filters,
  });
}

/**
 * Track filter application
 */
export function trackFilter(filterType: string, filterValue: any, entityType?: string): void {
  if (!MIXPANEL_TOKEN) return;

  trackEvent('Filter Applied', {
    filter_type: filterType,
    filter_value: filterValue,
    entity_type: entityType,
  });
}

/**
 * Track export
 */
export function trackExport(
  format: 'csv' | 'pdf' | 'excel',
  entityType: string,
  count: number
): void {
  if (!MIXPANEL_TOKEN) return;

  trackEvent('Data Exported', {
    format,
    entity_type: entityType,
    count,
  });
}

/**
 * Track navigation
 */
export function trackNavigation(destination: string, source?: string, section?: string): void {
  if (!MIXPANEL_TOKEN) return;

  trackEvent('Navigation Item Clicked', {
    destination,
    source: source || window.location.pathname,
    section,
  });
}

/**
 * Track error
 */
export function trackError(
  errorType: string,
  errorMessage: string,
  context?: Record<string, any>
): void {
  if (!MIXPANEL_TOKEN) return;

  trackEvent('Error Encountered', {
    error_type: errorType,
    error_message: errorMessage,
    page: window.location.pathname,
    ...context,
  });
}

/**
 * Track session event
 */
export function trackSession(action: 'start' | 'end' | 'timeout' | 'extended'): void {
  if (!MIXPANEL_TOKEN) return;

  const eventMap = {
    start: 'Session Started',
    end: 'Session Ended',
    timeout: 'Session Expired',
    extended: 'Session Extended',
  };

  trackEvent(eventMap[action], {
    timestamp: new Date().toISOString(),
  });
}

/**
 * Check if Mixpanel is enabled
 */
export function isMixpanelEnabled(): boolean {
  return !!MIXPANEL_TOKEN;
}

/**
 * Get Mixpanel instance for advanced usage
 */
export function getMixpanelInstance() {
  return mixpanel;
}
